import { createFileRoute, Outlet, redirect } from "@tanstack/react-router"

import { OrgNav } from "@/components/nav/org-nav"
import { getMembership } from "@/http/organizations/get-membership"
import { getOrganization } from "@/http/organizations/get-organization"
import { logger } from "@/lib/logger"
import { useOrganization } from "@/store/organization"

/**
 * Organization-scoped layout route
 * Validates organization slug and user membership before rendering children
 */
export const Route = createFileRoute("/_authenticated/$orgSlug")({
  beforeLoad: async ({ params }) => {
    const { orgSlug } = params
    logger.info("Loading organization route", { orgSlug })

    const { organizations, loadOrganizations } = useOrganization.getState()
    if (organizations.length === 0) {
      logger.info("Loading organizations for home redirect logic")
      await loadOrganizations()
    }

    // Validate org slug exists and user membership
    const [orgResponse, membershipResponse] = await Promise.all([
      getOrganization(orgSlug),
      getMembership(orgSlug),
    ])

    // If organization doesn't exist or user is not a member, redirect
    if (orgResponse.error || membershipResponse.error) {
      logger.warn("Invalid organization or membership", {
        orgSlug,
        orgError: orgResponse.error?.message,
        membershipError: membershipResponse.error?.message,
      })
      throw redirect({ to: "/select-organization" })
    }

    logger.info("Organization and membership validated", {
      orgSlug,
      role: membershipResponse.data?.membership.role,
    })

    return {
      orgSlug,
      orgData: orgResponse.data,
      membershipData: membershipResponse.data,
    }
  },
  loader: ({ params, context: routeContext }) => {
    const { orgSlug } = params
    const { organizations, setCurrentOrg } = useOrganization.getState()

    // Find the organization in the store
    const org = organizations.find((o) => o.slug === orgSlug)

    if (org) {
      setCurrentOrg(org)
      logger.info("Set current organization from store", { orgSlug })
    } else if (routeContext.orgData && routeContext.membershipData) {
      // Use data already fetched in beforeLoad instead of re-fetching
      const orgWithRole = {
        ...routeContext.orgData.organization,
        role: routeContext.membershipData.membership.role,
      }
      setCurrentOrg(orgWithRole)
      logger.info("Set current organization from beforeLoad", { orgSlug })
    }

    return {
      orgSlug,
    }
  },
  component: OrgSlugLayout,
})

function OrgSlugLayout() {
  const { orgSlug } = Route.useParams()

  return (
    <div className="flex flex-col h-full">
      <OrgNav orgSlug={orgSlug} />
      <div className="flex-1 p-4 md:p-6">
        <Outlet />
      </div>
    </div>
  )
}
